import { useState, useEffect, useCallback } from 'react';
import { LogOut, ChevronLeft, Pencil } from 'lucide-react';
import { appApi, setTokenApp, sairDoEspelho, type ClienteApp, type ResumoApp } from './api';
import { HomeApp } from './HomeApp';
import { ChatApp } from './ChatApp';
import { MenuApp } from './MenuApp';
import { OrcamentosApp } from './OrcamentosApp';
import { AgendaApp } from './AgendaApp';
import { FinanceiroApp } from './FinanceiroApp';
import { AvisosPush } from './AvisosPush';
import { useAvisos } from './useAvisos';

// O esqueleto do aplicativo depois que alguem entrou ([DA-575]).
//
// Ate 18/08/2026 este papel nao existia: o `AppCliente` entregava direto o
// `ChatApp`, que era a tela raiz e ainda hospedava o ☰ com as outras areas dentro.
// Com a HOME virando a porta de entrada, a navegacao precisou de dono — e o dono e'
// aqui, acima de todas as areas, e nao dentro de uma delas.
//
// ⚠️ **O `useAvisos` vive NESTE nivel, e isso e' obrigatorio, nao arrumacao.** Ele
// registra o service worker e inscreve o aparelho; dois deles rodando (um na home,
// outro no chat) inscreveriam em duplicata a cada troca de tela, num caminho em que
// a Apple e' exigente com o momento do pedido de permissao. O comentario que dizia
// isso morava no `ChatApp` desde a Onda 1 — mudou de casa junto com a decisao.

const COBRE = '#b45309';

/**
 * O que a area de dentro manda o cabecalho mostrar.
 *
 * ⚠️ `aoEditarTitulo` so vem preenchido quando o titulo e' **dado do usuario** e nao
 * rotulo de tela — hoje, so o nome do cliente na folha do compromisso ([DA-587]).
 */
interface CtxArea {
  titulo: string;
  voltar: (() => void) | null;
  aoEditarTitulo?: (() => void) | null;
}

/** As areas do aplicativo. O chat desenha o proprio cabecalho; as outras usam o daqui. */
type Vista = 'home' | 'chat' | 'orcamentos' | 'agenda' | 'financeiro';

/** As areas que tem telas DENTRO delas, e portanto mandam no cabecalho. */
const AREAS_COM_NIVEIS: Vista[] = ['orcamentos', 'agenda'];

/** De quanto em quanto tempo a home relê o proprio estado, com ela na frente da
 *  pessoa. Nao e' um chat: 15s e' de sobra pra uma tela de menu, e com o app no
 *  bolso a leitura para de vez (quem avisa ali e' a notificacao). */
const INTERVALO_RESUMO = 15000;

export function Aplicativo({ cliente, aoSair, aoAtualizar }: {
  cliente: ClienteApp;
  aoSair: () => void;
  aoAtualizar: (c: ClienteApp) => void;
}): React.ReactElement {
  const [vista, setVista] = useState<Vista>('home');
  const [resumo, setResumo] = useState<ResumoApp | null>(null);
  const avisos = useAvisos();

  // "Orcamentos" e "Agenda" tem telas DENTRO delas (lista → documento, lista →
  // compromisso), e a seta do cabecalho precisa voltar uma casa por vez — se ela
  // sempre despejasse na home, sair de um documento fecharia a area inteira. Entao a
  // area diz, daqui de dentro, o que o cabecalho mostra e o que a seta faz. (Veio do
  // `MenuApp`, onde a area morava ate a home existir.)
  //
  // ⚠️ **Um estado so pras duas**: elas nunca estao abertas ao mesmo tempo, e dois
  // contextos separados criariam a duvida de qual deles o cabecalho obedece.
  //
  // ⚠️ **`aoEditarTitulo` e' opcional e quase sempre nulo.** So a folha do compromisso
  // o preenche ([DA-587]): la o titulo NAO e' rotulo de tela, e' o nome do cliente —
  // dado dele, digitavel, e que ele pode ter errado. Area que manda palavra fixa
  // ("Agenda", "Orcamentos") nunca deve mandar isto: um lapis num titulo que ninguem
  // pode editar promete um caminho que nao existe.
  const [ctxArea, setCtxArea] = useState<CtxArea>({ titulo: 'Orçamentos', voltar: null });
  // `useCallback` obrigatorio: a area chama isto de um `useEffect`, e um setter novo
  // a cada render viraria laco infinito.
  const aoContextoArea = useCallback((c: CtxArea) => setCtxArea(c), []);

  // O estado das linhas da home. Relido ao VOLTAR pra ela — e' o momento em que os
  // numeros mudaram (saiu do chat: zerou o nao lidas; saiu de Orcamentos: pode ter
  // criado um) — e de tempos em tempos enquanto ela esta aberta.
  //
  // ⚠️ Falha em silencio de proposito: a home nao pode virar uma tela de erro
  // porque um contador nao voltou. Sem resumo, cada linha mostra o nome e a
  // descricao dela, que e' o que a home ja fazia antes de existir contador nenhum.
  const naHome = vista === 'home';
  useEffect(() => {
    if (!naHome) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let vivo = true;

    async function ler(): Promise<void> {
      try {
        const r = await appApi.resumo();
        if (vivo) setResumo(r);
      } catch {
        /* contador e' enfeite util, nao conteudo: a tela segue sem ele */
      }
      if (!vivo) return;
      // App no bolso nao relê: a bateria e' do cliente, e quem avisa com a tela
      // fechada e' a notificacao ([DA-532]).
      timer = setTimeout(() => { if (!document.hidden) void ler(); else agendarNaVolta(); }, INTERVALO_RESUMO);
    }

    function agendarNaVolta(): void {
      const aoVoltar = (): void => {
        if (document.hidden || !vivo) return;
        document.removeEventListener('visibilitychange', aoVoltar);
        void ler();
      };
      document.addEventListener('visibilitychange', aoVoltar);
    }

    void ler();
    return () => { vivo = false; if (timer) clearTimeout(timer); };
  }, [naHome]);

  function sair(): void {
    // ⚠️ **No espelho este botao sai do ESPELHO, nao da conta** ([DA-634]).
    // "Encerrar a sessao" aqui apagaria a credencial emprestada e deixaria a minha
    // guardada sem dono na tela — e encerrar a sessao de OUTRA pessoa nao e' o que
    // este botao faz (nem daria: o token dela nunca esteve neste aparelho).
    if (cliente.espelho) {
      sairDoEspelho();
      window.location.reload();
      return;
    }
    setTokenApp(null);
    aoSair();
  }

  // O CHAT desenha a tela inteira, cabecalho proprio incluido — ele tem barra de
  // digitacao, modo de selecao e folha de opcoes, e enfiar tudo isso num cabecalho
  // compartilhado so criaria condicoes pro cabecalho de todo mundo.
  if (vista === 'chat') {
    return (
      <ChatApp
        cliente={cliente}
        aoSair={aoSair}
        aoVoltar={() => setVista('home')}
      />
    );
  }

  const titulo = AREAS_COM_NIVEIS.includes(vista) ? ctxArea.titulo
    : vista === 'financeiro' ? 'Financeiro'
      : 'FIO DE COBRE';
  // ⛔ Amarrado ao MESMO `if` do titulo: fora das areas com niveis o cabecalho mostra
  // palavra fixa, e um lapis ali apontaria pra um `ctxArea` que sobrou de outra tela.
  const aoEditarTitulo = AREAS_COM_NIVEIS.includes(vista) ? ctxArea.aoEditarTitulo : null;

  return (
    <div className="app-cliente-root" style={{ height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: '#f7f7f7', color: '#111' }}>
      <header style={{ backgroundColor: COBRE, color: '#fff', padding: '12px 12px 12px 4px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {naHome ? (
          // O ☰ so existe na HOME. Ele guarda o que se ajusta uma vez e nao se usa
          // todo dia (a marca do prestador, o aviso de mensagens, a porta da
          // equipe); as areas de trabalho subiram pro menu da tela, que e' onde o
          // dedo procura. Nas outras vistas o mesmo canto vira a seta de voltar —
          // dois papeis no mesmo lugar, nunca ao mesmo tempo.
          <MenuApp avisos={avisos} cliente={cliente} aoAtualizar={aoAtualizar} />
        ) : (
          <button
            type="button"
            onClick={() => {
              // Dentro de Orcamentos e da Agenda a seta volta UMA casa (do documento
              // pra lista); so no primeiro nivel ela sai pra home.
              const interno = AREAS_COM_NIVEIS.includes(vista) ? ctxArea.voltar : null;
              if (interno) interno();
              else setVista('home');
            }}
            aria-label="Voltar"
            style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}
          >
            <ChevronLeft size={22} />
          </button>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* ⚠️ **Uma linha, com reticencias — o titulo virou TEXTO LIVRE** ([DA-587]).
              Ate 19/08 aqui so passava palavra fixa ("Agenda", "Orcamentos"), entao
              transbordo nao existia; desde que o compromisso salvo passou a ser
              nomeado pelo cliente dele, chega nome de ate 200 caracteres. Medido em
              prod a 390px: sem isto, `CONDOMINIO RESIDENCIAL JARDIM DAS ACACIAS...`
              quebrava em 3 linhas e **engordava a faixa de 64px pra 98px**, comendo
              34px de conteudo de toda tela de dentro. */}
          {/* ⚠️ **Com `aoEditarTitulo`, a linha inteira vira botao — nao so o lapis.**
              Um alvo de 16px na faixa e' pequeno demais pro dedo; o titulo ao lado ja
              esta ali e e' o que ele quer tocar. O lapis fica como o SINAL de que da'
              pra tocar, nao como o unico alvo. */}
          <div
            {...(aoEditarTitulo ? {
              role: 'button', tabIndex: 0,
              onClick: aoEditarTitulo,
              'aria-label': `Editar: ${titulo}`,
            } : {})}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
              cursor: aoEditarTitulo ? 'pointer' : undefined,
            }}
          >
            <span style={{
              fontSize: 17, fontWeight: 800, letterSpacing: 0.3, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {titulo}
            </span>
            {aoEditarTitulo && (
              <Pencil size={15} style={{ flexShrink: 0, opacity: 0.8 }} />
            )}
          </div>
          {naHome && (
            <div style={{ fontSize: 12.5, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cliente.nome}
            </div>
          )}
        </div>

        {/* Sair mora na HOME, que e' o ponto de partida. Nas areas de dentro o canto
            fica vazio de proposito: quem esta editando um orcamento nao pode ter o
            botao de encerrar a sessao a um toque de distancia do de voltar. */}
        {naHome && (
          <button type="button" onClick={sair} aria-label="Sair" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
            <LogOut size={19} />
          </button>
        )}
      </header>

      {/* O convite pra ligar os avisos ([DA-532]) mudou do chat pra ca junto com a
          porta de entrada: ele so aparece pra quem ainda nao ligou, e precisa estar
          na PRIMEIRA tela — atras de um toque no Chat, ele so alcancaria quem ja
          entrou na conversa, que e' justamente quem menos precisa ser avisado. */}
      {/* ⛔ **Não no espelho** ([DA-634]): este convite liga o aviso NESTE aparelho —
          o meu —, e a inscrição ficaria gravada na conta que estou olhando. Além de
          ser gravação (que o servidor recusa), ele leria como "este cliente não
          ligou os avisos", quando o estado mostrado é o do meu celular. */}
      {naHome && !cliente.espelho && <AvisosPush avisos={avisos} />}

      {vista === 'home' ? (
        <HomeApp
          cliente={cliente}
          resumo={resumo}
          aoAbrirChat={() => setVista('chat')}
          aoAbrirOrcamentos={() => setVista('orcamentos')}
          aoAbrirAgenda={() => setVista('agenda')}
          aoAbrirFinanceiro={() => setVista('financeiro')}
        />
      ) : vista === 'orcamentos' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <OrcamentosApp aoContexto={aoContextoArea} />
        </div>
      ) : vista === 'agenda' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <AgendaApp aoContexto={aoContextoArea} />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <FinanceiroApp />
        </div>
      )}
    </div>
  );
}
